import { useState } from "react"
import type { PortalMintUploadBody } from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import {
  preparePortalFile,
  sha256,
  uploadInteractionLimit,
  validatePortalFile,
  type UploadItem,
} from "./upload"

export type UploadTarget = { requestId: string; itemId: string }

/** The two calls that send one file: sign it, then confirm it once the
    bytes are stored. A signed-in client and a link visitor each have their
    own pair; the queue around them is the same. */
export type UploadLane = {
  mint(
    body: Pick<
      PortalMintUploadBody,
      "fileName" | "byteSize" | "contentType" | "contentHash"
    >,
    target?: UploadTarget
  ): Promise<{
    uploadToken: string
    uploadUrl: string
    uploadHeaders: Record<string, string>
  }>
  confirm(uploadToken: string): Promise<unknown>
}

/**
 * The files this visit sends, a few at a time, each with its own progress
 * and its own retry. `onSettled` runs once a batch is through, so the page
 * can read its list again.
 */
export function useUploadQueue(
  lane: UploadLane,
  onSettled: () => Promise<void>
) {
  const api = usePortalApi()
  const [uploads, setUploads] = useState<UploadItem[]>([])
  const [queueNote, setQueueNote] = useState<string | null>(null)
  // What a screen reader hears when a file lands or fails. The queue rows
  // are drawn and redrawn with every percent, so they are not the live
  // region; this one line is.
  const [announcement, setAnnouncement] = useState("")

  const uploadOne = async (item: UploadItem) => {
    const patch = (next: Partial<UploadItem>) =>
      setUploads((current) =>
        current.map((upload) =>
          upload.id === item.id ? { ...upload, ...next } : upload
        )
      )
    patch({ state: "preparing", progress: 0, message: undefined })
    let file = item.file
    try {
      file = await preparePortalFile(file)
    } catch {
      file = item.file
    }
    const validation = validatePortalFile(file)
    if ("error" in validation) {
      patch({ state: "failed", message: validation.error })
      setAnnouncement(`${item.name} not sent. ${validation.error}`)
      return
    }
    try {
      const contentHash = await sha256(file)
      const mint = await lane.mint(
        {
          fileName: file.name,
          byteSize: file.size,
          contentType: validation.contentType,
          contentHash,
        },
        item.requestId && item.requestItemId
          ? { requestId: item.requestId, itemId: item.requestItemId }
          : undefined
      )
      patch({ state: "uploading", progress: 0.02 })
      // The queue reads in whole percents, and every patch re-renders the
      // list, so the ticks between two percents are dropped.
      let shown = 2
      await api.putFile(
        mint.uploadUrl,
        file,
        mint.uploadHeaders,
        (progress) => {
          const percent = Math.round(progress * 100)
          if (percent === shown) return
          shown = percent
          patch({ progress })
        }
      )
      await lane.confirm(mint.uploadToken)
      patch({ state: "received", progress: 1 })
      setAnnouncement(`${item.name} sent`)
    } catch (uploadError) {
      const message = errorMessage(
        uploadError instanceof Error
          ? uploadError
          : new Error("Something went wrong. Try again.")
      )
      patch({ state: "failed", message })
      setAnnouncement(`${item.name} not sent. ${message}`)
    }
  }

  const handleFiles = async (
    selected: FileList | File[],
    target?: UploadTarget
  ) => {
    const files = Array.from(selected)
    if (files.length === 0) return
    const pending = files.map<UploadItem>((file) => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      progress: 0,
      state: "preparing",
      requestId: target?.requestId,
      requestItemId: target?.itemId,
    }))
    setUploads((current) => [...current, ...pending])
    setQueueNote(
      files.length > uploadInteractionLimit
        ? `${uploadInteractionLimit} files go at a time. The other ${files.length - uploadInteractionLimit} wait their turn.`
        : null
    )
    for (
      let offset = 0;
      offset < pending.length;
      offset += uploadInteractionLimit
    ) {
      await Promise.all(
        pending.slice(offset, offset + uploadInteractionLimit).map(uploadOne)
      )
    }
    await onSettled().catch(() => undefined)
  }

  const dismiss = (id: string) =>
    setUploads((current) => current.filter((item) => item.id !== id))

  return { uploads, queueNote, announcement, uploadOne, handleFiles, dismiss }
}
